<template>
  <div class="modifiers-sidebar">
    <div
      v-if="show"
      class="modifiers-sidebar-panel"
    >
      <div class="modifiers h-100">
        <div class="modifiers-top fixed-top">
          <b-button
            variant="danger"
            class="modifier-delete-btn"
            @click="$emit('delete-item')"
          >
            Delete Item
          </b-button>

          <h2>
            {{ modifiers.product ? getProductDisplayName(modifiers.product) : modifiers.productName }}
          </h2>

          <b-button
            variant="light"
            class="modifier-save-btn"
            style="color: #2889FA !important; font-weight: bold;"
            @click="$emit('save')"
          >
            Save
          </b-button>
        </div>


        <div :class="['modifiers-panel', { 'h-100': modifierGroupVertical }]">
          <template v-if="modifierGroupVertical">
            <div class="modifiers-vertical-list">
              <div
                v-for="modifierGroup in modifiers.modifierGroupsAtCurrentItem"
                :key="modifierGroup.id"
                class="modifier-group-section mb-4"
              >
                <div class="modifier-group-header px-3">
                  <div class="modifier-group-title-row">
                    <div class="modifier-group-title">
                      <span class="badge badge-primary mr-2">{{ countSelectedModifiers(modifierGroup) }}</span>
                      {{ modifierGroup.name }}
                    </div>
                    <div class="modifier-group-rules-inline">
                      <span
                        :class="[
                          'modifier-pill',
                          'modifier-pill--required',
                          { 'modifier-pill--required-on': modifierGroupRules(modifierGroup).required }
                        ]"
                      >
                        {{ modifierGroupRules(modifierGroup).required ? 'Required' : 'Optional' }}
                      </span>
                      <span class="modifier-pill modifier-pill--selection">
                        {{ modifierGroupRules(modifierGroup).selectionText }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="modifiers-body">
                  <div
                    v-for="modifier in modifierGroup.modifiers.filter((m) => m.active)"
                    :key="modifier.id"
                    class="modifiers-item"
                    :class="{
                      'modifiers-item--86d': modifiers.product && isModifier86d(modifiers.product.id, modifier.id)
                    }"
                    :style="{ backgroundColor: modifier.color || '#fff' }"
                    @click="$emit('toggle', modifier, modifierGroup)"
                  >
                    <div
                      v-if="isModifierSelected(modifier, modifierGroup)"
                      :class="[ 'modifiers-status', 'modifiers-status_success']"
                    >
                      <svg class="icon icon-icon-check">
                        <use xlink:href="#icon-icon-check" />
                      </svg>
                    </div>

                    <div
                      v-if="modifier.image"
                      class="modifiers-image"
                    >
                      <lazy-image
                        :src="modifier.image"
                        :alt="modifier.name"
                      />
                    </div>

                    <h6
                      class="modifiers-name"
                      :style="{ color: getTextColor(modifier.color) }"
                    >
                      {{ modifier.name }}
                    </h6>

                    <div class="modifiers-price">
                      +{{ currency }}{{ modifier.price.toFixed(2) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <b-tabs
            pills
            v-else
          >
            <b-tab
              v-for="modifierGroup in modifiers.modifierGroupsAtCurrentItem"
              :key="modifierGroup.id"
            >
              <template v-slot:title>
                <span>
                  {{ countSelectedModifiers(modifierGroup) }}
                </span>
                {{ modifierGroup.name }}
              </template>

              <div class="modifier-group-header px-3 pt-2">
                <div class="modifier-group-title-row">
                  <div class="modifier-group-title">
                    {{ modifierGroup.name }}
                  </div>
                  <div class="modifier-group-rules-inline">
                    <span
                      :class="[
                        'modifier-pill',
                        'modifier-pill--required',
                        { 'modifier-pill--required-on': modifierGroupRules(modifierGroup).required }
                      ]"
                    >
                      {{ modifierGroupRules(modifierGroup).required ? 'Required' : 'Optional' }}
                    </span>
                    <span class="modifier-pill modifier-pill--selection">
                      {{ modifierGroupRules(modifierGroup).selectionText }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="modifiers-body">
                <div
                  v-for="modifier in modifierGroup.modifiers.filter((m) => m.active)"
                  :key="modifier.id"
                  class="modifiers-item"
                  :class="{
                    'modifiers-item--86d': modifiers.product && isModifier86d(modifiers.product.id, modifier.id)
                  }"
                  :style="{ backgroundColor: modifier.color || '#fff' }"
                  @click="$emit('toggle', modifier, modifierGroup)"
                >
                  <div
                    v-if="isModifierSelected(modifier, modifierGroup)"
                    :class="[ 'modifiers-status', 'modifiers-status_success']"
                  >
                    <svg class="icon icon-icon-check">
                      <use xlink:href="#icon-icon-check" />
                    </svg>
                  </div>

                  <div
                    v-if="modifier.image"
                    class="modifiers-image"
                  >
                    <lazy-image
                      :src="modifier.image"
                      :alt="modifier.name"
                    />
                  </div>

                  <h6
                    class="modifiers-name"
                    :style="{ color: getTextColor(modifier.color) }"
                  >
                    {{ modifier.name }}
                  </h6>

                  <div class="modifiers-price">
                    +{{ currency }}{{ modifier.price.toFixed(2) }}
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LazyImage from "@/components/Common/LazyImage.vue";

export default {
  name: "ModifierPanel",
  components: {
    LazyImage,
  },
  props: {
    // modifiersShow from the parent's order modifier mixin.
    show: {
      type: Boolean,
      default: false,
    },
    // Live modifier-edit state (product, productName, modifierGroupsAtCurrentItem)
    // owned by the parent's order modifier mixin.
    modifiers: {
      type: Object,
      default: () => ({
        modifierGroupsAtCurrentItem: [],
        product: null,
        productName: "",
      }),
    },
    modifierGroupVertical: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "",
    },
    // Parent-bound helpers/selectors. They read reactive parent/store state, so
    // calling them during this child's render registers those deps on the
    // child's render watcher — mutations (toggle) re-render the panel correctly.
    getProductDisplayName: {
      type: Function,
      default: (product) => (product ? product.name : ""),
    },
    countSelectedModifiers: {
      type: Function,
      default: () => 0,
    },
    modifierGroupRules: {
      type: Function,
      default: () => ({ required: false, selectionText: "" }),
    },
    isModifierSelected: {
      type: Function,
      default: () => false,
    },
    isModifier86d: {
      type: Function,
      default: () => false,
    },
    getTextColor: {
      type: Function,
      default: () => "#333",
    },
  },
};
</script>
